import { ArrowBackIcon, ArrowForwardIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, Heading, HStack, Slider, useMediaQuery } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import CardImage from '@/components/CardImage';
import { BlogPostsMainData } from '@/Types/types';

interface Props {
  data: BlogPostsMainData[];
}

export default function TrendingCarouselPostPage({ data }: Props) {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobileScreen] = useMediaQuery('(max-width: 984px)');
  const containerRef = useRef<HTMLDivElement>(null);
  const numDots = Math.ceil(data.length <= 3 ? 1 : data.length / 3);

  const { t } = useTranslation('common');

  useEffect(() => {
    if (containerRef.current) {
      setScrollPosition(containerRef.current.scrollLeft);
    }
  }, []);

  const handleScroll = (value: number) => {
    if (containerRef.current) {
      const currentIndex = Math.round(value / containerRef.current.clientWidth);

      setCurrentSlide(currentIndex);
      setScrollPosition(value);
      containerRef.current.scrollTo({ left: value, behavior: 'smooth' });
    }
  };

  const handleNext = () => {
    if (containerRef.current) {
      const slidesPerPage = Math.min(data.length, !isMobileScreen ? 3 : 1);
      const currentIndex = Math.floor(scrollPosition / (containerRef.current.clientWidth * slidesPerPage));
      const newPosition = (currentIndex + 1) * containerRef.current.clientWidth * slidesPerPage;

      setCurrentSlide(currentIndex + 1);
      setScrollPosition(newPosition);
      containerRef.current.scrollTo({ left: newPosition, behavior: 'smooth' });
    }
  };

  const handlePrev = () => {
    if (containerRef.current) {
      const clientWidth = containerRef.current?.clientWidth || 0;
      const slidesPerPage = Math.min(data.length, !isMobileScreen ? 3 : 1);
      const newPosition = Math.max(scrollPosition - clientWidth * slidesPerPage, 0);
      const currentIndex = Math.floor(newPosition / (clientWidth * slidesPerPage));

      setCurrentSlide(currentIndex);
      setScrollPosition(newPosition);
      containerRef.current.scrollTo({ left: newPosition, behavior: 'smooth' });
    }
  };

  const handleSlideChange = (index: number) => {
    const slidesPerPage = Math.min(data.length, 4);
    const newPosition = index * (containerRef.current?.clientWidth || 0) * slidesPerPage;

    setCurrentSlide(index);
    setScrollPosition(newPosition);
    containerRef.current?.scrollTo({ left: newPosition, behavior: 'smooth' });
  };

  return (
    <Flex
      as="section"
      width="100%"
      bgColor="gray.50"
      borderRadius="16px"
      marginTop={{ base: '32px', lg: '48px' }}
      paddingBottom={{ base: '32px', lg: '32px' }}
      paddingTop={{ base: '32px', lg: '32px' }}
      position="relative"
    >
      <Flex direction="column" margin="0 auto" width="90%" mb="32px">
        <Flex justifyContent="space-between" alignItems="center">
          <Heading fontSize="24px" fontWeight="700" lineHeight="32px">
            {t('blogHomepage.trendingPosts.title')}
          </Heading>
          {!isMobileScreen && (
            <HStack spacing="16px" mt={2}>
              {Array.from({ length: numDots }).map((_, index) => (
                <Box
                  key={index}
                  w={index === currentSlide ? '12px' : '8px'}
                  h={index === currentSlide ? '12px' : '8px'}
                  borderRadius="full"
                  bg={index === currentSlide ? '#319795' : 'gray.300'}
                  cursor="pointer"
                  onClick={() => handleSlideChange(index)}
                />
              ))}
            </HStack>
          )}
        </Flex>
        <Flex w="100%">
          <Box position="relative">
            <Button
              aria-label="previous"
              position="absolute"
              top="50%"
              left="0px"
              transform="translateX(-50%)"
              size="md"
              borderRadius="9999px"
              h="40px"
              w="40px"
              bgColor="gray.100"
              color="gray.700"
              box-shadow="0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)"
              zIndex="3"
              onClick={handlePrev}
            >
              <ArrowBackIcon />
            </Button>
          </Box>
          <Slider
            w={0}
            value={scrollPosition}
            min={0}
            max={(containerRef.current?.scrollWidth ?? 0) - (containerRef.current?.clientWidth ?? 0) || 0}
            step={containerRef.current?.clientWidth || 0}
            onChange={handleScroll}
            aria-labelledby="slider-label"
          />
          <Flex direction="row" gap="32px" h="320px" mt="32px" overflowX="hidden" ref={containerRef}>
            {data.map((item) => (
              <CardImage post={item} key={item.id} />
            ))}
          </Flex>
          <Box position="relative">
            <Button
              aria-label="forward"
              position="absolute"
              top="50%"
              right="-40px"
              transform="translateX(-50%)"
              size="md"
              borderRadius="9999px"
              h="40px"
              w="40px"
              bgColor="gray.100"
              color="gray.700"
              box-shadow="0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)"
              zIndex="3"
              onClick={handleNext}
            >
              <ArrowForwardIcon />
            </Button>
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
}
